// Ship — admin/manager FedEx label creator. @@SHIP_SECTION_v81@@
// From/To from the address book or typed fresh, optional client tie-in (its
// designation code prefixes the reference), N boxes (same rows as the client
// label form), live rate shop (falls back to a plain service list), native
// FedEx PDF download per box, and FedEx pickup scheduling.
import React, { useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../lib/api';

const BLANK_ADDR = { name: '', company: '', phone: '', street1: '', street2: '', city: '', state: '', postal: '', country: 'US', residential: false };
const BLANK_BOX = { weight_lbs: '', length_in: '', width_in: '', height_in: '' };
// @@SHIP_NOTIFY_v81_3@@ FedEx notification events — the five boxes on FedEx's own ship form.
const NOTIFY_EVENTS = [
  { v: 'ON_SHIPMENT', label: 'Shipment created' },
  { v: 'ON_TENDER', label: 'Received by FedEx' },
  { v: 'ON_ESTIMATED_DELIVERY', label: 'Est. delivery updates' },
  { v: 'ON_DELIVERY', label: 'Delivered' },
  { v: 'ON_EXCEPTION', label: 'Delivery exceptions' },
];
const NOTIFY_DEFAULT = ['ON_TENDER', 'ON_DELIVERY', 'ON_EXCEPTION'];
const EMAIL_OK = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(e || '').trim());
let notifyKey = 0;
const EXPRESS = /OVERNIGHT|2_DAY|EXPRESS_SAVER|FIRST|PRIORITY/;
const TRANSIT = { ONE_DAY: '1 day', TWO_DAYS: '2 days', THREE_DAYS: '3 days', FOUR_DAYS: '4 days', FIVE_DAYS: '5 days', SIX_DAYS: '6 days', SEVEN_DAYS: '7 days' };
const money = (n) => (n == null ? '—' : '$' + Number(n).toFixed(2));
const todayLocal = () => { const d = new Date(); return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0'); };
const muted = { color: 'var(--hq-muted)', fontSize: 12 };
const small = (w) => ({ maxWidth: w });
function addrLine(a) { return a ? [a.name, a.street1, [a.city, a.state].filter(Boolean).join(' '), a.postal].filter(Boolean).join(', ') : ''; }
function fromRow(r) { return { ...BLANK_ADDR, name: r.name || '', company: r.company || '', phone: r.phone || '', street1: r.street1 || '', street2: r.street2 || '', city: r.city || '', state: r.state || '', postal: r.postal || '', country: r.country || 'US', residential: !!r.residential }; }
function transitText(r) {
  const t = r.transitTime ? (TRANSIT[r.transitTime] || r.transitTime.toLowerCase().replace(/_/g, ' ')) : null;
  const d = r.deliveryDate ? String(r.deliveryDate).slice(0, 10) : null;
  return [t, d].filter(Boolean).join(' · ') || '—';
}
async function downloadBlob(res, name) {
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = name;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

// One address block: book picker + fields + "save to book".
function AddressBlock({ kind, title, value, onChange, book, pick, onPick, save, onSave, hint }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  return (
    <div style={{ flex: '1 1 380px', minWidth: 300 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <strong style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--hq-muted)' }}>{title}</strong>
        <select className="hq-select" value={pick} onChange={(e) => onPick(e.target.value)} style={{ minWidth: 200, height: 32 }}>
          <option value="">Type a new address…</option>
          {book.map((a) => (<option key={a.id} value={a.id}>{a.label}{a.is_default ? ' (default)' : ''}</option>))}
        </select>
      </div>
      {hint && <p style={{ ...muted, margin: '0 0 8px' }}>{hint}</p>}
      <div className="hq-form">
        <input className="hq-input" placeholder="Name *" value={value.name} onChange={(e) => set('name', e.target.value)} style={{ minWidth: 180 }} />
        <input className="hq-input" placeholder="Company" value={value.company} onChange={(e) => set('company', e.target.value)} style={{ minWidth: 160 }} />
        <input className="hq-input" placeholder="Phone *" value={value.phone} onChange={(e) => set('phone', e.target.value)} style={small(150)} />
      </div>
      <div className="hq-form" style={{ marginTop: 8 }}>
        <input className="hq-input" placeholder="Street *" value={value.street1} onChange={(e) => set('street1', e.target.value)} style={{ minWidth: 220 }} />
        <input className="hq-input" placeholder="Suite / Apt" value={value.street2} onChange={(e) => set('street2', e.target.value)} style={small(140)} />
      </div>
      <div className="hq-form" style={{ marginTop: 8 }}>
        <input className="hq-input" placeholder="City *" value={value.city} onChange={(e) => set('city', e.target.value)} style={{ minWidth: 150 }} />
        <input className="hq-input" placeholder="ST *" value={value.state} onChange={(e) => set('state', e.target.value.toUpperCase().slice(0, 2))} style={small(64)} />
        <input className="hq-input" placeholder="ZIP *" value={value.postal} onChange={(e) => set('postal', e.target.value)} style={small(110)} />
        <input className="hq-input" placeholder="US" value={value.country} onChange={(e) => set('country', e.target.value.toUpperCase().slice(0, 2))} style={small(64)} />
        {kind === 'to' && (
          <label style={{ ...muted, display: 'flex', alignItems: 'center', gap: 6 }}>
            <input type="checkbox" checked={!!value.residential} onChange={(e) => set('residential', e.target.checked)} /> Residential
          </label>
        )}
      </div>
      {!pick && (
        <div className="hq-form" style={{ marginTop: 8 }}>
          <label style={{ ...muted, display: 'flex', alignItems: 'center', gap: 6 }}>
            <input type="checkbox" checked={save.on} onChange={(e) => onSave({ ...save, on: e.target.checked })} /> Save to address book
          </label>
          {save.on && (
            <>
              <input className="hq-input" placeholder="Book label" value={save.label} onChange={(e) => onSave({ ...save, label: e.target.value })} style={small(180)} />
              <label style={{ ...muted, display: 'flex', alignItems: 'center', gap: 6 }}>
                <input type="checkbox" checked={save.is_default} onChange={(e) => onSave({ ...save, is_default: e.target.checked })} /> Make default
              </label>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function Ship() {
  const [meta, setMeta] = useState({ services: [], sandbox: false });
  const [book, setBook] = useState([]);
  const [clients, setClients] = useState([]);
  const [presets, setPresets] = useState([]);

  const [from, setFrom] = useState({ ...BLANK_ADDR });
  const [to, setTo] = useState({ ...BLANK_ADDR });
  const [fromPick, setFromPick] = useState('');
  const [toPick, setToPick] = useState('');
  const [saveFrom, setSaveFrom] = useState({ on: false, label: '', is_default: false });
  const [saveTo, setSaveTo] = useState({ on: false, label: '', is_default: false });
  const [clientId, setClientId] = useState('');
  const [reference, setReference] = useState('');
  const [billable, setBillable] = useState(true); // @@SHIP_REFS_v81_2@@ default on → " (B)" suffix
  const [invoiceNo, setInvoiceNo] = useState('');
  const [poNo, setPoNo] = useState('');
  const [boxes, setBoxes] = useState([{ ...BLANK_BOX }]);

  const [rates, setRates] = useState(null);       // null = not fetched; [] = fetched
  const [rateInfo, setRateInfo] = useState('');
  const [rateBusy, setRateBusy] = useState(false);
  const [service, setService] = useState('FEDEX_GROUND');

  const [labelBusy, setLabelBusy] = useState(false);
  const [labelMsg, setLabelMsg] = useState('');
  const [last, setLast] = useState(null);          // { tracking, ids, count, reference }
  const [recent, setRecent] = useState([]);
  // @@SHIP_NOTIFY_v81_3@@ rows: { key, id?, email, events[], on, saved }
  const [notify, setNotify] = useState([]);
  const [saveNotify, setSaveNotify] = useState(true);
  const [notifyMsg, setNotifyMsg] = useState('');

  const [pickup, setPickup] = useState({ address: { ...BLANK_ADDR }, pick: '', pickup_date: todayLocal(), ready_time: '09:00', close_time: '17:00', package_count: '1', total_weight_lbs: '', carrier_code: 'FDXG', remarks: '', master_tracking: '', reference: '' }); // @@SHIP_PICKUP_v81_4@@
  const [showArchived, setShowArchived] = useState(false);
  const [pickupsBusy, setPickupsBusy] = useState(false);
  const [pickupBusy, setPickupBusy] = useState(false);
  const [pickupMsg, setPickupMsg] = useState('');
  const [pickups, setPickups] = useState([]);
  const [pickupHint, setPickupHint] = useState('');

  const fromBook = useMemo(() => book.filter((a) => a.kind === 'from'), [book]);
  const toBook = useMemo(() => book.filter((a) => a.kind === 'to'), [book]);
  const client = useMemo(() => clients.find((c) => c.id === clientId) || null, [clients, clientId]);
  const baseRef = [client && client.designation_code, reference.trim()].filter(Boolean).join(' - '); // @@SHIP_REFS_v81_2@@
  const refPreview = baseRef ? (billable ? baseRef.slice(0, 36) + ' (B)' : baseRef.slice(0, 40)) : '';
  const totalWeight = boxes.reduce((s, b) => s + (Number(b.weight_lbs) || 0), 0);
  const serviceList = useMemo(() => (rates && rates.length ? rates : meta.services), [rates, meta]);
  const chosenRate = useMemo(() => (rates || []).find((r) => r.serviceType === service) || null, [rates, service]);

  async function loadBook() {
    const res = await apiFetch('/api/ship/addresses');
    if (res.ok) { const d = await res.json(); setBook(d.addresses || []); return d.addresses || []; }
    return [];
  }
  async function loadRecent() { const res = await apiFetch('/api/ship/recent'); if (res.ok) { const d = await res.json(); setRecent(d.packages || []); } }
  async function loadNotifyRecipients() { // @@SHIP_NOTIFY_v81_3@@
    const res = await apiFetch('/api/ship/notify-recipients');
    if (!res.ok) return;
    const d = await res.json();
    setNotify((cur) => {
      const adhoc = cur.filter((r) => !r.saved);
      const saved = (d.recipients || []).map((r) => ({ key: 'r' + r.id, id: r.id, email: r.email, events: r.events || [], on: !!r.enabled, saved: true }));
      return saved.concat(adhoc.filter((a) => !saved.some((sv) => sv.email === String(a.email).trim().toLowerCase())));
    });
  }
  async function loadPickups(archived) { // @@SHIP_PICKUP_v81_4@@ archived: undefined = current toggle
    const arch = archived === undefined ? showArchived : archived;
    setPickupsBusy(true);
    try { const res = await apiFetch('/api/ship/pickups' + (arch ? '?archived=1' : '')); if (res.ok) { const d = await res.json(); setPickups(d.pickups || []); } }
    finally { setPickupsBusy(false); }
  }
  async function archivePickup(p, undo) {
    const res = await apiFetch('/api/ship/pickups/' + p.id + (undo ? '/unarchive' : '/archive'), { method: 'POST' });
    setPickupMsg(res.ok ? ('Pickup ' + p.confirmation_code + (undo ? ' restored.' : ' archived.')) : 'Could not update.');
    loadPickups();
  }

  useEffect(() => {
    (async () => {
      const [m, c, p] = await Promise.all([apiFetch('/api/ship/meta'), apiFetch('/api/ship/clients'), apiFetch('/api/packages/parcel-presets')]);
      if (m.ok) setMeta(await m.json());
      if (c.ok) { const d = await c.json(); setClients(d.clients || []); }
      if (p.ok) { const d = await p.json(); setPresets(d.presets || []); }
      const list = await loadBook();
      const df = list.find((a) => a.kind === 'from' && a.is_default) || list.find((a) => a.kind === 'from'); // @@SHIP_REFS_v81_2@@ fallback: first From
      if (df) { setFromPick(df.id); setFrom(fromRow(df)); setPickup((pk) => ({ ...pk, pick: df.id, address: fromRow(df) })); }
      loadRecent(); loadPickups(); loadNotifyRecipients(); // @@SHIP_NOTIFY_v81_3@@
    })();
  }, []);

  // Any edit invalidates a fetched rate shop (rates are per address + boxes).
  function touch(fn) { return (...args) => { setRates(null); setRateInfo(''); fn(...args); }; }
  const setFromT = touch(setFrom), setToT = touch(setTo), setBoxesT = touch(setBoxes);

  function pickFrom(id) { setFromPick(id); const a = fromBook.find((x) => x.id === id); setFromT(a ? fromRow(a) : { ...BLANK_ADDR }); }
  function pickTo(id) { setToPick(id); const a = toBook.find((x) => x.id === id); setToT(a ? fromRow(a) : { ...BLANK_ADDR }); }
  function pickClient(id) {
    setClientId(id);
    const c = clients.find((x) => x.id === id);
    if (c && c.ship_to && c.ship_to.street1) { setToPick(''); setToT(fromRow(c.ship_to)); }
  }
  function setBox(i, patch) { setBoxesT((bs) => bs.map((bx, idx) => (idx === i ? { ...bx, ...patch } : bx))); }
  function addBox() { setBoxesT((bs) => [...bs, { ...BLANK_BOX }]); }
  function removeBox(i) { setBoxesT((bs) => (bs.length > 1 ? bs.filter((_, idx) => idx !== i) : bs)); }
  function applyPreset(i, id) {
    const p = presets.find((x) => x.id === id);
    if (!p) return;
    setBox(i, { weight_lbs: p.weight_lbs == null ? '' : String(p.weight_lbs), length_in: p.length_in == null ? '' : String(p.length_in), width_in: p.width_in == null ? '' : String(p.width_in), height_in: p.height_in == null ? '' : String(p.height_in) });
  }
  function parcels() {
    return boxes.map((bx) => { const o = {}; ['weight_lbs', 'length_in', 'width_in', 'height_in'].forEach((k) => { if (String(bx[k]).trim() !== '') o[k] = bx[k]; }); return o; });
  }

  // @@SHIP_NOTIFY_v81_3@@ notification row helpers
  function setNotifyRow(key, patch) { setNotify((rows) => rows.map((r) => (r.key === key ? { ...r, ...patch } : r))); }
  function toggleEvent(key, ev) { setNotify((rows) => rows.map((r) => (r.key !== key ? r : { ...r, events: r.events.includes(ev) ? r.events.filter((x) => x !== ev) : r.events.concat(ev) }))); }
  function addNotifyRow() { setNotify((rows) => rows.concat({ key: 'a' + (++notifyKey), email: '', events: NOTIFY_DEFAULT.slice(), on: true, saved: false })); }
  function removeNotifyRow(key) { setNotify((rows) => rows.filter((r) => r.key !== key)); }
  async function forgetRecipient(r) {
    if (!window.confirm('Remove ' + r.email + ' from saved notification recipients?')) return;
    const res = await apiFetch('/api/ship/notify-recipients/' + r.id, { method: 'DELETE' });
    if (res.ok) { removeNotifyRow(r.key); setNotifyMsg('Removed ' + r.email + '.'); }
  }
  async function saveRecipientDefaults(r) {
    const res = await apiFetch('/api/ship/notify-recipients/' + r.id, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ events: r.events, enabled: r.on }) });
    setNotifyMsg(res.ok ? ('Defaults saved for ' + r.email + '.') : 'Could not save defaults.');
  }
  const notifyPayload = notify.filter((r) => r.on && EMAIL_OK(r.email) && r.events.length).map((r) => ({ email: r.email.trim().toLowerCase(), events: r.events }));

  async function getRates() {
    setRateBusy(true); setRateInfo(''); setRates(null);
    try {
      const res = await apiFetch('/api/ship/rates', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ from, to, parcels: parcels() }) });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) {
        setRates([]);
        setRateInfo((d.error || 'Rates unavailable') + (d.detail ? ' — ' + d.detail : '') + '. Pick a service from the list instead.');
        return;
      }
      setRates(d.rates || []);
      if (d.rates && d.rates.length) {
        const keep = d.rates.find((r) => r.serviceType === service);
        setService(keep ? keep.serviceType : d.rates[0].serviceType);
      }
      setRateInfo((d.rates && d.rates.length ? d.rates.length + ' services quoted. ' : 'No services returned. ') + 'Rates are FedEx estimates; the invoice may differ.');
    } catch (e) { setRates([]); setRateInfo('Could not fetch rates.'); }
    finally { setRateBusy(false); }
  }

  async function createLabel() {
    setLabelMsg(''); setLabelBusy(true); setLast(null);
    try {
      const body = { from, to, parcels: parcels(), service_type: service, reference: reference.trim(), billable, invoice_number: invoiceNo.trim(), po_number: poNo.trim(), notifications: notifyPayload, save_notifications: saveNotify }; // @@SHIP_REFS_v81_2@@ @@SHIP_NOTIFY_v81_3@@
      if (clientId) body.client_id = clientId;
      if (chosenRate && chosenRate.accountRate != null) body.quoted_rate = chosenRate.accountRate;
      else if (chosenRate && chosenRate.listRate != null) body.quoted_rate = chosenRate.listRate;
      if (!fromPick && saveFrom.on) body.save_from = { label: saveFrom.label || from.name, is_default: saveFrom.is_default };
      if (!toPick && saveTo.on) body.save_to = { label: saveTo.label || to.name, is_default: saveTo.is_default };
      const res = await apiFetch('/api/ship/labels', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (!res.ok) { const d = await res.json().catch(() => ({})); setLabelMsg(d.error || 'Could not create label.'); return; }
      const tracking = res.headers.get('X-Tracking-Number') || '';
      const ids = (res.headers.get('X-Package-Ids') || '').split(',').filter(Boolean);
      const count = Number(res.headers.get('X-Package-Count') || ids.length || 1);
      await downloadBlob(res, (tracking || 'fedex-label') + (count > 1 ? '-box1' : '') + '.pdf');
      setLast({ tracking, ids, count, reference: refPreview });
      setLabelMsg('Label created — tracking ' + tracking + (count > 1 ? ' (' + count + ' boxes; box 1 downloaded, others below)' : '') + '. Added to Packages.');
      // Prefill the pickup card from this shipment.
      setPickup((pk) => ({ ...pk, address: { ...from }, pick: fromPick, package_count: String(count), total_weight_lbs: totalWeight ? String(Math.round(totalWeight * 100) / 100) : pk.total_weight_lbs, carrier_code: EXPRESS.test(service) ? 'FDXE' : 'FDXG', master_tracking: tracking, reference: refPreview })); // @@SHIP_PICKUP_v81_4@@
      setPickupHint('Prefilled from shipment ' + tracking + ' — ' + count + ' box' + (count > 1 ? 'es' : '') + ', ' + (EXPRESS.test(service) ? 'Express' : 'Ground') + '.');
      setReference(''); setInvoiceNo(''); setPoNo(''); setBoxes([{ ...BLANK_BOX }]); setRates(null); setRateInfo(''); // @@SHIP_REFS_v81_2@@
      if (saveNotify && notify.some((r) => !r.saved && r.on && EMAIL_OK(r.email))) { setNotify((rows) => rows.filter((r) => r.saved)); loadNotifyRecipients(); } // @@SHIP_NOTIFY_v81_3@@
      if (saveFrom.on || saveTo.on) { setSaveFrom({ on: false, label: '', is_default: false }); setSaveTo({ on: false, label: '', is_default: false }); loadBook(); }
      loadRecent();
    } catch (e) { setLabelMsg('Could not create label.'); }
    finally { setLabelBusy(false); }
  }

  async function downloadPkg(id, name) {
    const res = await apiFetch('/api/packages/' + id + '/label');
    if (!res.ok) { setLabelMsg('Could not download that label.'); return; }
    await downloadBlob(res, name);
  }

  function pickPickupAddr(id) { const a = fromBook.find((x) => x.id === id); setPickup((pk) => ({ ...pk, pick: id, address: a ? fromRow(a) : { ...BLANK_ADDR } })); }
  async function schedulePickup() {
    setPickupBusy(true); setPickupMsg('');
    try {
      const res = await apiFetch('/api/ship/pickups', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...pickup, pick: undefined }) });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { setPickupMsg((d.error || 'Could not schedule pickup.') + (d.detail && d.code !== 'bad_input' ? ' — ' + d.detail : '')); return; }
      setPickupMsg('Pickup scheduled — confirmation ' + d.pickup.confirmation_code + (d.pickup.fedex_message ? ' (' + d.pickup.fedex_message + ')' : '') + '.');
      setPickupHint(''); setPickup((pk) => ({ ...pk, remarks: '', master_tracking: '', reference: '' })); // @@SHIP_PICKUP_v81_4@@
      loadPickups();
    } catch (e) { setPickupMsg('Could not schedule pickup.'); }
    finally { setPickupBusy(false); }
  }
  async function cancelPickup(p) {
    if (!window.confirm('Cancel FedEx pickup ' + p.confirmation_code + ' on ' + p.pickup_date + '?')) return;
    const res = await apiFetch('/api/ship/pickups/' + p.id + '/cancel', { method: 'POST' });
    const d = await res.json().catch(() => ({}));
    setPickupMsg(res.ok ? 'Pickup ' + p.confirmation_code + ' cancelled.' : ((d.error || 'Could not cancel.') + (d.detail ? ' — ' + d.detail : '')));
    loadPickups();
  }
  async function deleteAddress(a) {
    if (!window.confirm('Remove "' + a.label + '" from the address book?')) return;
    const res = await apiFetch('/api/ship/addresses/' + a.id, { method: 'DELETE' });
    if (res.ok) { if (fromPick === a.id) setFromPick(''); if (toPick === a.id) setToPick(''); loadBook(); }
  }
  async function makeDefault(a) {
    const res = await apiFetch('/api/ship/addresses/' + a.id, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ is_default: true }) });
    if (res.ok) loadBook();
  }

  const canRate = from.postal && to.postal && boxes.every((b) => Number(b.weight_lbs) > 0);
  const canLabel = canRate && service && (refPreview.length > 0);
  const pa = pickup.address;
  const canPickup = pa.name && pa.phone && pa.street1 && pa.city && pa.state && pa.postal && pickup.pickup_date && pickup.ready_time && pickup.close_time;

  return (
    <div className="hq-page-content">
      <div className="hq-page-head">
        <h1>Ship</h1>
        <p>Create FedEx labels from any address, rate-shop services, and schedule pickups.{meta.sandbox ? ' ' : ''}{meta.sandbox && <strong style={{ color: '#e8c15a' }}>FedEx SANDBOX — labels are not real.</strong>}</p>
      </div>

      {/* 1. Addresses */}
      <div className="hq-panel" style={{ marginBottom: 20 }}>
        <div className="hq-panel-head"><h2>Addresses</h2></div>
        <div style={{ padding: 20, display: 'flex', gap: 28, flexWrap: 'wrap' }}>
          <AddressBlock kind="from" title="Ship from" value={from} onChange={setFromT} book={fromBook} pick={fromPick} onPick={pickFrom} save={saveFrom} onSave={setSaveFrom} />
          <AddressBlock kind="to" title="Ship to" value={to} onChange={setToT} book={toBook} pick={toPick} onPick={pickTo} save={saveTo} onSave={setSaveTo}
            hint={client && client.ship_to && client.ship_to.street1 ? 'Prefilled from ' + client.name + "'s stored ship-to." : null} />
        </div>
      </div>

      {/* 2. Client + reference */}
      <div className="hq-panel" style={{ marginBottom: 20 }}>
        <div className="hq-panel-head"><h2>Reference</h2></div>
        <div style={{ padding: 20 }}>
          <div className="hq-form">
            <select className="hq-select" value={clientId} onChange={(e) => pickClient(e.target.value)} style={{ minWidth: 220 }}>
              <option value="">No client</option>
              {clients.map((c) => (<option key={c.id} value={c.id}>{c.name}{c.designation_code ? ' (' + c.designation_code + ')' : ''}</option>))}
            </select>
            <input className="hq-input" placeholder="Reference (shows on the label)" value={reference} onChange={(e) => setReference(e.target.value)} style={{ minWidth: 280 }} maxLength={40} />
            <label style={{ ...muted, display: 'flex', alignItems: 'center', gap: 6 }} title="Appends (B) to the label reference"> {/* @@SHIP_REFS_v81_2@@ */}
              <input type="checkbox" checked={billable} onChange={(e) => setBillable(e.target.checked)} /> Billable
            </label>
            <input className="hq-input" placeholder="Invoice #" value={invoiceNo} onChange={(e) => setInvoiceNo(e.target.value)} style={small(150)} maxLength={40} />
            <input className="hq-input" placeholder="PO #" value={poNo} onChange={(e) => setPoNo(e.target.value)} style={small(150)} maxLength={40} />
            <span style={muted}>On label: <strong style={{ color: 'var(--hq-txt)' }}>{refPreview || '—'}</strong>{client && !client.designation_code ? ' (this client has no designation code)' : ''}</span>
          </div>
        </div>
      </div>

      {/* 3. Boxes */}
      <div className="hq-panel" style={{ marginBottom: 20 }}>
        <div className="hq-panel-head"><h2>Boxes</h2><span style={muted}>{boxes.length} box{boxes.length > 1 ? 'es' : ''} · {Math.round(totalWeight * 100) / 100} lb total</span></div>
        <div style={{ padding: 20 }}>
          {boxes.map((bx, i) => (
            <div className="hq-form" key={i} style={{ marginTop: i ? 10 : 0, alignItems: 'center' }}>
              <span style={{ ...muted, minWidth: 48 }}>Box {i + 1}</span>
              <select className="hq-select" defaultValue="" onChange={(e) => { applyPreset(i, e.target.value); e.target.value = ''; }} style={{ minWidth: 170 }}>
                <option value="">Parcel preset…</option>
                {presets.map((p) => (<option key={p.id} value={p.id}>{p.name}</option>))}
              </select>
              <input className="hq-input" placeholder="Weight lb *" value={bx.weight_lbs} onChange={(e) => setBox(i, { weight_lbs: e.target.value })} style={small(110)} />
              <input className="hq-input" placeholder="L in" value={bx.length_in} onChange={(e) => setBox(i, { length_in: e.target.value })} style={small(80)} />
              <input className="hq-input" placeholder="W in" value={bx.width_in} onChange={(e) => setBox(i, { width_in: e.target.value })} style={small(80)} />
              <input className="hq-input" placeholder="H in" value={bx.height_in} onChange={(e) => setBox(i, { height_in: e.target.value })} style={small(80)} />
              {boxes.length > 1 && (<button className="hq-signout" style={{ width: 'auto', padding: '0 10px' }} onClick={() => removeBox(i)} title="Remove box">×</button>)}
            </div>
          ))}
          <div style={{ marginTop: 10 }}>
            <button className="hq-signout" style={{ width: 'auto', padding: '0 14px' }} onClick={addBox} disabled={boxes.length >= 40}>+ Add box</button>
            {boxes.length > 1 && <span style={{ ...muted, marginLeft: 10 }}>One multi-piece shipment, linked tracking, one PDF per box.</span>}
          </div>
        </div>
      </div>

      {/* 4. Service + rates */}
      <div className="hq-panel" style={{ marginBottom: 20 }}>
        <div className="hq-panel-head">
          <h2>Service</h2>
          <button className="hq-signout" style={{ width: 'auto', padding: '0 14px' }} onClick={getRates} disabled={!canRate || rateBusy}>{rateBusy ? 'Getting rates…' : 'Get rates'}</button>
        </div>
        <div style={{ padding: 20 }}>
          {rates && rates.length > 0 ? (
            <table className="hq-table">
              <thead><tr><th></th><th>Service</th><th>Your rate</th><th>List rate</th><th>Transit</th></tr></thead>
              <tbody>
                {rates.map((r) => (
                  <tr key={r.serviceType} onClick={() => setService(r.serviceType)} style={{ cursor: 'pointer', background: service === r.serviceType ? 'rgba(255,255,255,.05)' : undefined }}>
                    <td><input type="radio" name="svc" checked={service === r.serviceType} onChange={() => setService(r.serviceType)} /></td>
                    <td>{r.serviceName}<div style={muted}>{r.serviceType}</div></td>
                    <td><strong>{money(r.accountRate)}</strong></td>
                    <td style={muted}>{money(r.listRate)}</td>
                    <td>{transitText(r)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="hq-form">
              <select className="hq-select" value={service} onChange={(e) => setService(e.target.value)} style={{ minWidth: 300 }}>
                {serviceList.map((s) => (<option key={s.serviceType} value={s.serviceType}>{s.serviceName}</option>))}
              </select>
              <span style={muted}>{rates === null ? 'Fill in both addresses and box weights, then Get rates to see prices — or just pick a service.' : ''}</span>
            </div>
          )}
          {rateInfo && <p className="hq-msg">{rateInfo}</p>}
        </div>
      </div>

      {/* @@SHIP_NOTIFY_v81_3@@ 4b. FedEx email notifications */}
      <div className="hq-panel" style={{ marginBottom: 20 }}>
        <div className="hq-panel-head">
          <h2>Email notifications</h2>
          <button className="hq-signout" style={{ width: 'auto', padding: '0 14px' }} onClick={addNotifyRow}>+ Add email</button>
        </div>
        <div style={{ padding: '0 0 12px' }}>
          <table className="hq-table">
            <thead><tr><th></th><th>Email</th>{NOTIFY_EVENTS.map((ev) => (<th key={ev.v} style={{ fontSize: 10 }}>{ev.label}</th>))}<th></th></tr></thead>
            <tbody>
              {notify.map((r) => (
                <tr key={r.key} style={r.on ? null : { opacity: .5 }}>
                  <td><input type="checkbox" checked={r.on} onChange={(e) => setNotifyRow(r.key, { on: e.target.checked })} title="Notify on this shipment" /></td>
                  <td>{r.saved ? (<span>{r.email}<div style={muted}>saved</div></span>) : (
                    <input className="hq-input" placeholder="name@example.com" value={r.email} onChange={(e) => setNotifyRow(r.key, { email: e.target.value })} style={{ minWidth: 220, height: 32 }} />
                  )}</td>
                  {NOTIFY_EVENTS.map((ev) => (
                    <td key={ev.v} style={{ textAlign: 'center' }}><input type="checkbox" checked={r.events.includes(ev.v)} disabled={!r.on} onChange={() => toggleEvent(r.key, ev.v)} /></td>
                  ))}
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {r.saved ? (<>
                      <button className="hq-signout" style={{ width: 'auto', padding: '0 10px', marginRight: 6 }} onClick={() => saveRecipientDefaults(r)} title="Make these the defaults for every new shipment">Set defaults</button>
                      <button className="hq-signout" style={{ width: 'auto', padding: '0 10px', borderColor: 'rgba(255,90,82,.3)', color: '#ff5a52' }} onClick={() => forgetRecipient(r)}>Forget</button>
                    </>) : (
                      <button className="hq-signout" style={{ width: 'auto', padding: '0 10px' }} onClick={() => removeNotifyRow(r.key)} title="Remove">×</button>
                    )}
                  </td>
                </tr>
              ))}
              {notify.length === 0 && (<tr><td className="hq-empty" colSpan={NOTIFY_EVENTS.length + 3}>No one is notified. Add an email — FedEx sends the ticked events for this shipment.</td></tr>)}
            </tbody>
          </table>
          <div className="hq-form" style={{ padding: '12px 20px 0' }}>
            {notify.some((r) => !r.saved) && (
              <label style={{ ...muted, display: 'flex', alignItems: 'center', gap: 6 }}>
                <input type="checkbox" checked={saveNotify} onChange={(e) => setSaveNotify(e.target.checked)} /> Save new emails for next time
              </label>
            )}
            <span style={muted}>{notifyPayload.length ? (notifyPayload.length + ' recipient' + (notifyPayload.length > 1 ? 's' : '') + ' on this shipment.') : 'Ticked rows with at least one event are sent to FedEx with the label.'} English only; max 6.</span>
          </div>
          {notifyMsg && <p className="hq-msg" style={{ padding: '0 20px' }}>{notifyMsg}</p>}
        </div>
      </div>

      {/* 5. Create */}
      <div className="hq-panel" style={{ marginBottom: 20 }}>
        <div className="hq-panel-head">
          <h2>Create label</h2>
          <button className="hq-btn" onClick={createLabel} disabled={!canLabel || labelBusy}>{labelBusy ? 'Creating…' : ('Create ' + (boxes.length > 1 ? boxes.length + ' labels' : 'label'))}</button>
        </div>
        <div style={{ padding: 20 }}>
          <p style={{ ...muted, fontSize: 13, margin: 0 }}>
            {addrLine(from) || 'From —'} <span style={{ opacity: .5 }}>→</span> {addrLine(to) || 'To —'} · {boxes.length} box{boxes.length > 1 ? 'es' : ''} · {(serviceList.find((s) => s.serviceType === service) || {}).serviceName || service}
            {chosenRate && chosenRate.accountRate != null ? ' · ' + money(chosenRate.accountRate) : ''} · ref <strong style={{ color: 'var(--hq-txt)' }}>{refPreview || '—'}</strong>
            {!refPreview ? ' — add a reference or pick a client.' : ''}
            {notifyPayload.length ? (<> · notify {notifyPayload.map((n) => n.email).join(', ')}</>) : ''} {/* @@SHIP_NOTIFY_v81_3@@ */}
          </p>
          {labelMsg && <p className="hq-msg">{labelMsg}</p>}
          {last && last.count > 1 && (
            <div className="hq-form" style={{ marginTop: 10 }}>
              {last.ids.map((id, i) => (
                <button key={id} className="hq-signout" style={{ width: 'auto', padding: '0 12px' }} onClick={() => downloadPkg(id, last.tracking + '-box' + (i + 1) + '.pdf')}>Box {i + 1} PDF</button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* 6. Pickup */}
      <div className="hq-panel" style={{ marginBottom: 20 }}>
        <div className="hq-panel-head">
          <h2>Schedule a FedEx pickup</h2>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}> {/* @@SHIP_PICKUP_v81_4@@ */}
            <label style={{ ...muted, display: 'flex', alignItems: 'center', gap: 6 }}>
              <input type="checkbox" checked={showArchived} onChange={(e) => { setShowArchived(e.target.checked); loadPickups(e.target.checked); }} /> Show archived
            </label>
            <button className="hq-signout" style={{ width: 'auto', padding: '0 12px' }} onClick={() => loadPickups()} disabled={pickupsBusy}>{pickupsBusy ? 'Refreshing…' : 'Refresh'}</button>
            <button className="hq-btn" onClick={schedulePickup} disabled={!canPickup || pickupBusy}>{pickupBusy ? 'Scheduling…' : 'Schedule pickup'}</button>
          </div>
        </div>
        <div style={{ padding: 20 }}>
          {pickupHint && <p className="hq-msg" style={{ margin: '0 0 12px' }}>{pickupHint}</p>}
          <div className="hq-form">
            <select className="hq-select" value={pickup.pick} onChange={(e) => pickPickupAddr(e.target.value)} style={{ minWidth: 200 }}>
              <option value="">Pickup address…</option>
              {fromBook.map((a) => (<option key={a.id} value={a.id}>{a.label}</option>))}
            </select>
            <span style={{ ...muted, flex: '1 1 300px' }}>{addrLine(pa) || 'Pick a saved From address, or use the current Ship-from.'}</span>
            <button className="hq-signout" style={{ width: 'auto', padding: '0 12px' }} onClick={() => setPickup((pk) => ({ ...pk, pick: fromPick, address: { ...from } }))}>Use Ship-from</button>
          </div>
          <div className="hq-form" style={{ marginTop: 10 }}>
            <label style={muted}>Date <input className="hq-input" type="date" value={pickup.pickup_date} min={todayLocal()} onChange={(e) => setPickup({ ...pickup, pickup_date: e.target.value })} style={{ marginLeft: 6 }} /></label>
            <label style={muted}>Ready <input className="hq-input" type="time" value={pickup.ready_time} onChange={(e) => setPickup({ ...pickup, ready_time: e.target.value })} style={{ marginLeft: 6 }} /></label>
            <label style={muted}>Closes <input className="hq-input" type="time" value={pickup.close_time} onChange={(e) => setPickup({ ...pickup, close_time: e.target.value })} style={{ marginLeft: 6 }} /></label>
            <input className="hq-input" placeholder="Packages" value={pickup.package_count} onChange={(e) => setPickup({ ...pickup, package_count: e.target.value })} style={small(100)} />
            <input className="hq-input" placeholder="Total lb" value={pickup.total_weight_lbs} onChange={(e) => setPickup({ ...pickup, total_weight_lbs: e.target.value })} style={small(100)} />
            <select className="hq-select" value={pickup.carrier_code} onChange={(e) => setPickup({ ...pickup, carrier_code: e.target.value })}>
              <option value="FDXG">Ground / Home Delivery</option>
              <option value="FDXE">Express</option>
            </select>
            <input className="hq-input" placeholder="Reference (internal)" value={pickup.reference} onChange={(e) => setPickup({ ...pickup, reference: e.target.value })} style={{ minWidth: 200 }} maxLength={80} /> {/* @@SHIP_PICKUP_v81_4@@ */}
            <input className="hq-input" placeholder="Remarks for the driver" value={pickup.remarks} onChange={(e) => setPickup({ ...pickup, remarks: e.target.value })} style={{ minWidth: 220 }} maxLength={60} />
          </div>
          <p style={{ ...muted, margin: '10px 0 0' }}>Times are local to the pickup address. Ground and Express are separate pickups at FedEx — schedule one per carrier.</p>
          {pickupMsg && <p className="hq-msg">{pickupMsg}</p>}
        </div>
        <table className="hq-table">
          <thead><tr><th>Date</th><th>Window</th><th>Reference</th><th>Confirmation</th><th>Carrier</th><th>Pkgs</th><th>Address</th><th>Status</th><th></th></tr></thead> {/* @@SHIP_PICKUP_v81_4@@ */}
          <tbody>
            {pickups.map((p) => (
              <tr key={p.id} style={p.status === 'cancelled' ? { opacity: .5 } : null}>
                <td>{p.pickup_date}</td>
                <td>{p.ready_time}–{p.close_time}</td>
                <td style={{ fontSize: 12 }}>{p.reference || '—'}{p.master_tracking ? <div style={muted}>{p.master_tracking}</div> : null}</td> {/* @@SHIP_PICKUP_v81_4@@ */}
                <td>{p.confirmation_code}{p.location_code ? <span style={muted}> · {p.location_code}</span> : null}</td>
                <td>{p.carrier_code === 'FDXE' ? 'Express' : 'Ground'}</td>
                <td>{p.package_count} · {p.total_weight_lbs} lb</td>
                <td style={{ fontSize: 12 }}>{addrLine(p.address)}</td>
                <td><span className={'hq-pill ' + (p.status === 'scheduled' ? 'hq-on' : 'hq-off')}>{p.status}</span></td>
                <td style={{ textAlign: 'right' }}>
                  {p.status === 'scheduled' && !p.archived_at && (<button className="hq-signout" style={{ width: 'auto', padding: '0 12px', marginRight: 6, borderColor: 'rgba(255,90,82,.3)', color: '#ff5a52' }} onClick={() => cancelPickup(p)}>Cancel</button>)}
                  <button className="hq-signout" style={{ width: 'auto', padding: '0 12px' }} onClick={() => archivePickup(p, !!p.archived_at)}>{p.archived_at ? 'Restore' : 'Archive'}</button> {/* @@SHIP_PICKUP_v81_4@@ */}
                </td>
              </tr>
            ))}
            {pickups.length === 0 && (<tr><td className="hq-empty" colSpan={9}>{showArchived ? 'No archived pickups.' : 'No pickups scheduled yet.'}</td></tr>)}
          </tbody>
        </table>
      </div>

      {/* 7. Recent labels */}
      <div className="hq-panel" style={{ marginBottom: 20 }}>
        <div className="hq-panel-head"><h2>Recent FedEx labels</h2></div>
        <table className="hq-table">
          <thead><tr><th>Label</th><th>Client</th><th>Inv / PO</th><th>Service</th><th>Rate</th><th>To</th><th>Tracking</th><th>Status</th><th></th></tr></thead> {/* @@SHIP_REFS_v81_2@@ */}
          <tbody>
            {recent.map((p) => (
              <tr key={p.id}>
                <td>{p.label || '—'}</td>
                <td>{p.client_name || '—'}</td>
                <td style={{ fontSize: 12 }}>{[p.invoice_number, p.po_number].filter(Boolean).join(' / ') || '—'}</td> {/* @@SHIP_REFS_v81_2@@ */}
                <td style={{ fontSize: 12 }}>{p.service_type || '—'}</td>
                <td>{money(p.quoted_rate)}</td>
                <td style={{ fontSize: 12 }}>{p.destination || '—'}</td>
                <td>{p.tracking_number}</td>
                <td><span className={'hq-pill hq-status-' + p.status}>{p.status}</span></td>
                <td style={{ textAlign: 'right' }}>
                  {p.has_label_file ? (<button className="hq-signout" style={{ width: 'auto', padding: '0 12px' }} onClick={() => downloadPkg(p.id, p.tracking_number + '.pdf')}>PDF</button>) : (<span style={muted}>no pdf</span>)}
                </td>
              </tr>
            ))}
            {recent.length === 0 && (<tr><td className="hq-empty" colSpan={9}>No FedEx labels yet.</td></tr>)}
          </tbody>
        </table>
      </div>

      {/* 8. Address book management */}
      <div className="hq-panel" style={{ marginBottom: 20 }}>
        <div className="hq-panel-head"><h2>Address book</h2><span style={muted}>Add entries by ticking “Save to address book” above.</span></div>
        <table className="hq-table">
          <thead><tr><th>Kind</th><th>Label</th><th>Address</th><th></th></tr></thead>
          <tbody>
            {book.map((a) => (
              <tr key={a.id}>
                <td style={{ textTransform: 'uppercase', fontSize: 12 }}>{a.kind}{a.is_default ? <span style={muted}> · default</span> : null}</td>
                <td>{a.label}</td>
                <td style={{ fontSize: 12 }}>{addrLine(a)}{a.residential ? ' (residential)' : ''}</td>
                <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                  {!a.is_default && (<button className="hq-signout" style={{ width: 'auto', padding: '0 12px', marginRight: 6 }} onClick={() => makeDefault(a)}>Default</button>)}
                  <button className="hq-signout" style={{ width: 'auto', padding: '0 12px', borderColor: 'rgba(255,90,82,.3)', color: '#ff5a52' }} onClick={() => deleteAddress(a)}>Remove</button>
                </td>
              </tr>
            ))}
            {book.length === 0 && (<tr><td className="hq-empty" colSpan={4}>Empty — save a From and a To to make next time one click.</td></tr>)}
          </tbody>
        </table>
      </div>
    </div>
  );
}
